import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area';
import * as React from 'react';
import { cn } from '~/utils/cn';

export function ScrollArea({
  className,
  children,
  id,
  viewportRef: externalViewportRef,
  viewportClassName,
  horizontalScroll,
  autoScrollOnDrag,
  disableTrackClick,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  viewportRef?: React.RefObject<HTMLDivElement | null>;
  viewportClassName?: string;
  /** Enable horizontal scrolling (min-width: fit-content on content). Defaults to false. */
  horizontalScroll?: boolean;
  /** Enable auto-scrolling when dragging elements near edges (uses @atlaskit/pragmatic-drag-and-drop). */
  autoScrollOnDrag?: boolean | 'vertical' | 'horizontal';
  /** Disable clicking the scrollbar track to jump scroll position. Only the thumb remains draggable. */
  disableTrackClick?: boolean;
}) {
  const internalViewportRef = React.useRef<HTMLDivElement | null>(null);
  const viewportRef = externalViewportRef ?? internalViewportRef;

  React.useEffect(() => {
    if (!autoScrollOnDrag || !viewportRef.current) return;
    const axis = typeof autoScrollOnDrag === 'string' ? autoScrollOnDrag : undefined;
    return autoScrollForElements({ element: viewportRef.current, ...(axis && { getAllowedAxis: () => axis }) });
  }, [autoScrollOnDrag, viewportRef]);

  // Base UI's content ResizeObserver cannot fire while the content div is height-pinned to 100%, so a subtree
  // mutation that changes the scrollable size re-dispatches a scroll event to trigger the primitive's recompute.
  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (typeof MutationObserver === 'undefined' || !viewport) return;
    let lastWidth = viewport.scrollWidth;
    let lastHeight = viewport.scrollHeight;
    let frame = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const { scrollWidth, scrollHeight } = viewport;
        if (scrollWidth === lastWidth && scrollHeight === lastHeight) return;
        lastWidth = scrollWidth;
        lastHeight = scrollHeight;
        viewport.dispatchEvent(new Event('scroll'));
      });
    });
    mo.observe(viewport, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
    };
  }, [viewportRef]);

  return (
    <ScrollAreaPrimitive.Root data-slot="scroll-area" className={cn('relative', className)} {...props}>
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        id={id ? `${id}-viewport` : undefined}
        ref={viewportRef}
        className={cn(
          'size-full touch-manipulation rounded-[inherit] outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-inset',
          viewportClassName,
        )}
      >
        <ScrollAreaPrimitive.Content style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: horizontalScroll ? undefined : 0 }}>
          {children}
        </ScrollAreaPrimitive.Content>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar disableTrackClick={disableTrackClick} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

export function ScrollBar({
  className,
  orientation = 'vertical',
  disableTrackClick,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Scrollbar> & { disableTrackClick?: boolean }) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        'z-20 flex select-none p-px transition-colors',
        orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent',
        orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent',
        disableTrackClick && 'pointer-events-none',
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-slot="scroll-area-thumb"
        className={cn('relative flex-1 rounded-full bg-border', disableTrackClick && 'pointer-events-auto')}
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}
